import React, { useState } from 'react';
import { Box, Fab, Paper, Typography, TextField, IconButton, Stack, CircularProgress, Button } from '@mui/material';
import { AutoAwesome as ChatIcon, Close as CloseIcon, Send as SendIcon, WhatsApp } from '@mui/icons-material';

export const AIChatbot: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [messageCount, setMessageCount] = useState(0);

  const [messages, setMessages] = useState<{ sender: 'user' | 'ai'; text: string }[]>([
    { sender: 'ai', text: 'Hello! Welcome to Fingard Partners. I am a Senior CA Advisor. How can I help you with your tax or business compliance today?' }
  ]);

  const handleSendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setMessages(prev => [...prev, { sender: 'user', text: userMessage }]);
    setInput('');
    setLoading(true);

    // AUTOMATIC WHATSAPP HANDOFF AFTER 3 MESSAGES
    if (messageCount >= 3) {
      setTimeout(() => {
        setMessages(prev => [...prev, { 
          sender: 'ai', 
          text: "We've covered some great initial details! Since your situation requires personalized statutory review, let's get you in touch with our senior CA team. Please connect on WhatsApp or book a free consultation: https://wa.me/919116033575" 
        }]);
        setLoading(false);
      }, 700);
      return;
    }

    setMessageCount(prev => prev + 1);

    try {
      const apiBase = process.env.REACT_APP_API_URL || 
        (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
          ? 'http://localhost:5001'
          : 'https://fingard-backend.onrender.com');

      const response = await fetch(`${apiBase}/api/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          message: userMessage, 
          history: messages.slice(-6) 
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.text) {
          setMessages(prev => [...prev, { sender: 'ai', text: data.text }]);
        } else {
          throw new Error(data.error || 'Chat response unavailable');
        }
      } else {
        throw new Error(`Server returned ${response.status}`);
      }
    } catch (err: any) {
      console.warn('AIChatbot backend fallback triggered:', err.message);
      setMessages(prev => [...prev, { 
        sender: 'ai', 
        text: "Our team of Chartered Accountants is standing by to help with your exact filing needs. Feel free to connect directly via WhatsApp at +91 91160 33575 for prompt assistance!" 
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ position: 'fixed', bottom: 96, right: 32, zIndex: 2000 }}>
      {!open && (
        <Fab 
          color="primary" 
          onClick={() => setOpen(true)} 
          sx={{ 
            boxShadow: '0 8px 24px rgba(25, 118, 210, 0.3)',
            background: 'linear-gradient(45deg, #123D6B 30%, #29b6f6 90%)',
            transition: 'all 0.2s ease-in-out',
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%': { boxShadow: '0 0 0 0 rgba(41, 182, 246, 0.7)' },
              '70%': { boxShadow: '0 0 0 15px rgba(41, 182, 246, 0)' },
              '100%': { boxShadow: '0 0 0 0 rgba(41, 182, 246, 0)' },
            },
            '&:hover': { transform: 'scale(1.05)', animation: 'none' }
          }}
        >
          <ChatIcon />
        </Fab>
      )}

      {open && (
        <Paper 
          elevation={6} 
          sx={{ 
            width: 340, 
            height: 500, 
            display: 'flex', 
            flexDirection: 'column', 
            borderRadius: 4, 
            overflow: 'hidden', 
            border: '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
            position: 'absolute',
            bottom: 0,
            right: 0
          }}
        >
          {/* Header */}
          <Box sx={{ bgcolor: 'primary.main', background: 'linear-gradient(135deg, #123D6B 0%, #123D6B 100%)', color: 'white', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', letterSpacing: '0.5px' }}>Fingard Chat Bot</Typography>
              <Typography sx={{ fontSize: '0.7rem', opacity: 0.8 }}>Online • Usually replies instantly</Typography>
            </Box>
            <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'white' }}><CloseIcon fontSize="small" /></IconButton>
          </Box>

          {/* Messages Wrapper Pane */}
          <Box sx={{ flexGrow: 1, p: 2, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5, bgcolor: '#f8f9fa' }}>
            {messages.map((msg, i) => (
              <Box key={i} sx={{ alignSelf: msg.sender === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                <Box 
                  sx={{ 
                    bgcolor: msg.sender === 'user' ? '#123D6B' : 'white', 
                    color: msg.sender === 'user' ? 'white' : 'text.primary', 
                    p: 1.5, 
                    borderRadius: msg.sender === 'user' ? '14px 14px 0 14px' : '14px 14px 14px 0',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    border: msg.sender === 'user' ? 'none' : '1px solid rgba(0,0,0,0.05)'
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: '0.84rem', lineHeight: 1.45 }}>
                    {/* Basic link parsing for the WhatsApp URL so it is clickable */}
                    {msg.text.includes('https://wa.me/') ? (
                      <>
                        {msg.text.split('https://wa.me/')[0]}
                        <a href={`https://wa.me/${msg.text.split('https://wa.me/')[1]}`} target="_blank" rel="noreferrer" style={{ color: msg.sender === 'user' ? '#fff' : '#123D6B', fontWeight: 'bold' }}>
                          https://wa.me/{msg.text.split('https://wa.me/')[1]}
                        </a>
                      </>
                    ) : (
                      msg.text
                    )}
                  </Typography>
                </Box>
              </Box>
            ))}
            {loading && <CircularProgress size={20} sx={{ mx: 'auto', my: 1 }} />}
          </Box>

          {/* Permanent WhatsApp Escalation Button */}
          <Box sx={{ px: 2, py: 1, bgcolor: '#f8f9fa' }}>
            <Button 
              href="https://wa.me/919116033575" 
              target="_blank" 
              fullWidth 
              variant="outlined" 
              startIcon={<WhatsApp />}
              sx={{ 
                color: '#25D366', 
                borderColor: '#25D366', 
                textTransform: 'none', 
                fontWeight: 600,
                borderRadius: '20px',
                py: 0.5,
                '&:hover': { bgcolor: 'rgba(37, 211, 102, 0.05)', borderColor: '#25D366' } 
              }}
            >
              Talk to Human on WhatsApp
            </Button>
          </Box>

          {/* Input Panel Field */}
          <Stack direction="row" spacing={1} sx={{ p: 1.5, bgcolor: 'white', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
            <TextField 
              fullWidth 
              size="small" 
              placeholder={messageCount >= 3 ? "Chat ended." : "Ask your CA..."}
              value={input} 
              disabled={messageCount >= 3 || loading}
              onChange={e => setInput(e.target.value)} 
              onKeyDown={e => e.key === 'Enter' && handleSendMessage()} 
              InputProps={{ sx: { fontSize: '0.85rem', borderRadius: 3 } }} 
            />
            <IconButton color="primary" onClick={handleSendMessage} disabled={loading || messageCount >= 3 || !input.trim()}>
              <SendIcon fontSize="small" sx={{ color: messageCount >= 3 ? 'grey' : '#123D6B' }} />
            </IconButton>
          </Stack>
        </Paper>
      )}
    </Box>
  );
};
